import cx from 'clsx'
import { graphql, useStaticQuery } from 'gatsby'
import { PropsWithChildren, useEffect } from 'react'
import { Helmet } from 'react-helmet'

import { Container, Footer, Header, Layout } from '@karakuri-web/faq-component'

import { Session } from '../session'
import { Data } from '../widgets/interface'

function chooseTemplate(botId: string, template: string) {
  if (['rarejob', '5z13fcpat1'].some(a => a === botId)) {
    return ''
  }
  return template
}
function toClassName(botId: string) {
  return `b-${botId}`
}
interface IndexLayoutProps {
  className: string
}

const IndexLayoutQuery = graphql`
  query IndexLayoutQuery {
    settingYaml(ignore: { ne: true }) {
      botId
      tenantId
      title
      headerImageUrl
      headerImageAltText
      headerImageLinkUrl
      faviconUrl
      styles
      scripts
      css
      js
      header
      footer
      siteName
      template
      words {
        header {
          caption
        }
        meta {
          googleSiteVerification
          keywords
          robots
        }
      }
    }
  }
`

export default function IndexLayout({
  className: layoutClassName,
  children,
}: PropsWithChildren<IndexLayoutProps>) {
  const data = useStaticQuery<Data>(IndexLayoutQuery)
  useEffect(() => Session.init(), [])

  const styleLinks = (data.settingYaml.styles || []).filter((s): s is string => !!s)
  const headerImageLinkUrl = data.settingYaml.headerImageLinkUrl || ''
  const headerImageUrl = data.settingYaml.headerImageUrl || ''
  const headerImageAlt =
    data.settingYaml.headerImageAltText || data.settingYaml.siteName || data.settingYaml.title || ''
  const header = data.settingYaml.header || ''
  const footer = data.settingYaml.footer || ''
  const js = data.settingYaml.js || ''
  const css = data.settingYaml.css || ''
  const scripts = (data.settingYaml.scripts || []).filter((s): s is string => !!s)
  const faviconUrl = data.settingYaml.faviconUrl || ''
  const botId = data.settingYaml.botId || ''
  const templateClassName = chooseTemplate(botId, data.settingYaml.template || 'template-2')
  const className = toClassName(botId)
  const headerCaption = data.settingYaml.words?.header?.caption || 'よくあるご質問（Q&A）'
  const meta = data.settingYaml.words?.meta || {}

  return (
    <Layout className={layoutClassName}>
      <Helmet
        title={data.settingYaml.title || ''}
        meta={Object.entries(meta)
          .filter((target): target is [string, string] => !!target[1])
          .map(([name, content]) => ({
            name: name.replace(/([A-Z])/g, '-$1').toLowerCase(),
            content,
          }))}
        bodyAttributes={{ class: cx('faq') }}
        htmlAttributes={{ class: `${className} ${templateClassName}` }}
      >
        {styleLinks.map(style => (
          <link rel="stylesheet" href={style} key={style} />
        ))}
        {faviconUrl &&
          ['icon', 'shortcut icon', 'apple-touch-icon'].map(rel => (
            <link key={rel} rel={rel} href={faviconUrl} />
          ))}
        {css && <style>{css}</style>}
        {js && <script>{js}</script>}
        {scripts.map(s => (
          <script src={s} key={s} />
        ))}
      </Helmet>
      <Header
        header={header}
        headerCaption={headerCaption}
        headerImageSrc={headerImageUrl}
        headerImageAlt={headerImageAlt}
        headerImageLink={headerImageLinkUrl}
      />
      <Container>{children}</Container>
      <Footer footer={footer} />
    </Layout>
  )
}
